import React, { useEffect, useRef } from 'react';
import { RouteGridEngine } from './routeGridEngine.js';
import { Link } from 'react-router-dom';

// Bundler-agnostic asset base for the GLBs in /public/obj:
// - Vite exposes import.meta.env.BASE_URL ('/' or the configured `base`)
// - CRA/webpack exposes process.env.PUBLIC_URL ('' in dev, or the
//   `homepage` sub-path in production builds)
function useIsSmallScreen(maxWidth = 768) {
  const [small, setSmall] = React.useState(
    () => window.matchMedia(`(max-width: ${maxWidth}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setSmall(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [maxWidth]);
  return small;
}

function getAssetBase() {
  try {
    if (import.meta.env && import.meta.env.BASE_URL) {
      return import.meta.env.BASE_URL;
    }
  } catch (e) { /* not running under Vite */ }
  if (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL != null) {
    return process.env.PUBLIC_URL + '/';
  }
  return '/';
}

/**
 * RouteGrid — procedural route visualization.
 *
 * Drop this component anywhere in your app. It fills its positioned parent
 * (the wrapper is position:absolute inset:0), so give the parent a size, or
 * mount it into a full-screen container like the included index.css does.
 *
 * The GLB models live in /public/obj and are loaded at runtime relative to
 * import.meta.env.BASE_URL, so they work under any deploy sub-path as long as
 * vite `base` is set correctly.
 */
export default function RouteGrid({
  spacing = 130,
  pieceScale = 0.98,
  inkColor = '#1b3ae9',
  bgColor = '#fceaea',
}) {
  const isSmall = useIsSmallScreen();          // ≤768px → true
  const gridSpacing = isSmall ? 90 : spacing;  // 90 on phones, 130 otherwise
// export default function RouteGrid({
  // spacing = 130,
  // pieceScale = 0.98,
  // inkColor = '#1b3ae9',
  // bgColor = '#fceaea',
// }) {
  const wrapRef = useRef(null);
  const glRef = useRef(null);
  const lineRef = useRef(null);
  const flatRef = useRef(null);
  const eyesRef = useRef(null);
  const aboutRef = useRef(null);
  const engineRef = useRef(null);

  // keep latest props readable by the imperative engine
  // const propsRef = useRef({ spacing, pieceScale, inkColor, bgColor });
  // propsRef.current = { spacing, pieceScale, inkColor, bgColor };
  const propsRef = useRef({ spacing: gridSpacing, pieceScale, inkColor, bgColor });
  propsRef.current = { spacing: gridSpacing, pieceScale, inkColor, bgColor };

  useEffect(() => {
    const els = {
      wrap: wrapRef.current,
      gl: glRef.current,
      line: lineRef.current,
      flat: flatRef.current,
      eyes: eyesRef.current,
      about: aboutRef.current,
    };
    const engine = new RouteGridEngine(
      els,
      () => propsRef.current,
      getAssetBase()
    );
    engineRef.current = engine;
    engine.mount();
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  // rebuild when tweakable props change (after the engine is up)
  // useEffect(() => {
    // const e = engineRef.current;
    // if (e && e.renderer) e.build();
  // }, [spacing, pieceScale, inkColor, bgColor]);
  useEffect(() => {
    const e = engineRef.current;
    if (e && e.renderer) e.build();
  }, [gridSpacing, pieceScale, inkColor, bgColor]);
  
  useEffect(() => {
    const header = document.querySelector('header'); // hide header
    if (header) header.style.display = 'none';
    return () => { if (header) header.style.display = ''; };
  }, []);

  const fill = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
  };

  return (
    // <div ref={wrapRef} style={{ position: 'absolute', inset: 0 }}>
    <div ref={wrapRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 40px' }}>
        <p style={{ zIndex: 10, font: "800 13px/1 'Elms Sans', san-serif", fontWeight: 700, letterSpacing: '0.14em', fontSize: 13, textTransform: 'uppercase', color: '#1f1f1f' }}>
          <Link to="/">Natsumi H.</Link>
        </p>
        <button
         onClick={() => engineRef.current && engineRef.current.shuffle()}
         style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 10,
          font: "600 13px/1 ui-sans-serif, system-ui, sans-serif",
          letterSpacing: '0.02em',
          color: '#1f1f1f',
          background: 'rgba(255,255,255,1)',
          border: '0px solid rgba(0,0,0,0.0)',
          borderRadius: 999,
          padding: '11px 18px',
          cursor: 'pointer',
        }}
      >
        Shuffle
      </button>

     </header>
      <canvas ref={glRef} style={{ ...fill, transition: 'opacity 0.2s ease' }} />
      <canvas ref={lineRef} style={{ ...fill, pointerEvents: 'none' }} />
      <div
        ref={eyesRef}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', transition: 'opacity 0.2s ease' }}
      />
      <canvas
        ref={flatRef}
        style={{ ...fill, opacity: 0, transition: 'opacity 0.2s ease', pointerEvents: 'none' }}
      />

      <div
        ref={aboutRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 50,
          background: 'rgba(46, 76, 208, 0.9)',
          color: '#ffffff',
          fontFamily: "'Elms Sans', system-ui, sans-serif",
          fontWeight: 200,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          transform: 'translateX(100%)',
          transition: 'transform .55s cubic-bezier(.22,.61,.36,1)',
          willChange: 'transform',
        }}
      >
          <button
            onClick={() => engineRef.current && engineRef.current.closeAbout()}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              zIndex: 10,
              font: "500 13px/1 'Elms Sans', san-serif",
              letterSpacing: '0.02em',
              color: '#1f1f1f',
              background: 'rgba(255,255,255,1)',
              border: '0px solid rgba(0,0,0,0.0)',
              borderRadius: 999,
              padding: '11px 18px',
              cursor: 'pointer',
            }}
          >
            ← Back
          </button>
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 820, width: '100%', margin: '0 auto', padding: 30, boxSizing: 'border-box' }}>
          <h1 style={{ fontFamily: "'Goudy Bookletter 1911', serif", fontWeight: 400, fontSize: 'clamp(40px, 6vw, 60px)', lineHeight: 1.02, margin: '0 0 30px', textWrap: 'pretty' }}>
            Forming
          </h1>
          <p style={{ fontSize: 'clamp(14px, 1.7vw, 16px)', lineHeight: 1.5, maxWidth: '80ch', margin: '0 0 18px', color: '#ffffff', textWrap: 'pretty' }}>
            Why are people different?
          </p>
          <p style={{ fontSize: 'clamp(14px, 1.7vw, 16px)', lineHeight: 1.5, maxWidth: '80ch', margin: '0 0 18px', color: '#ffffff', textWrap: 'pretty' }}>
            I was born and raised in Japan, where I grew up in an environment with shared appearances and values, accepting this sense of familiarity as “normal” without questioning it. However, after moving to London, a multicultural city where people from diverse cultures and backgrounds coexist, I began to be curious about why such profound differences exist between individuals.
          </p>
          <p style={{ fontSize: 'clamp(14px, 1.7vw, 16px)', lineHeight: 1.5, maxWidth: '80ch', margin: '0 0 18px', color: '#ffffff', textWrap: 'pretty' }}>

All forms of life — from dogs and birds to insects — are born with different characteristics and forms. This variation carries its own role within communities, rather than existing within a hierarchy of better or worse. These differences become increasingly complex through interactions with their environments and communities, and I believe that this diversity is a fundamental principle through which the world is formed.
          </p>
          <p style={{ fontSize: 'clamp(14px, 1.7vw, 16px)', lineHeight: 1.5, maxWidth: '80ch', margin: 0, color: '#ffffff', textWrap: 'pretty' }}>
            This artwork explores the structure of society through route-generating algorithms and parametric 3D geometry. By filling space with evolving forms, it examines how social structures continuously emerge and transform through the interactions between individual entities.
          </p>
          
        </main>
      </div>
    </div>
  );
}
